import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig, motion, useMotionValueEvent, useScroll } from 'motion/react';

import HowItWorksImage1 from '@/assets/images/branding/how-it-works-1.svg';
import HowItWorksImage2 from '@/assets/images/branding/how-it-works-2.svg';
import HowItWorksImage3 from '@/assets/images/branding/how-it-works-3.svg';
import HowItWorksImage4 from '@/assets/images/branding/how-it-works-4.svg';
import HowItWorksImage5 from '@/assets/images/branding/how-it-works-5.svg';
import HowItWorksIcon1 from '@/assets/images/branding/how-it-works-icon-1.svg';
import HowItWorksIcon2 from '@/assets/images/branding/how-it-works-icon-2.svg';
import HowItWorksIcon3 from '@/assets/images/branding/how-it-works-icon-3.svg';
import HowItWorksIcon4 from '@/assets/images/branding/how-it-works-icon-4.svg';
import HowItWorksIcon5 from '@/assets/images/branding/how-it-works-icon-5.svg';

const HOW_IT_WORKS_STEPS = [
  {
    title: 'Set up your budget structure',
    description:
      'Define your revenue target and expense cap, then create Accounting Centers for every project, contract, department, product, or service.',
    image: HowItWorksImage1,
    icon: HowItWorksIcon1
  },
  {
    title: 'Assign your team',
    description:
      'Invite team members to your budget and assign them roles as Staff or Validators to keep work organized.',
    image: HowItWorksImage2,
    icon: HowItWorksIcon2
  },
  {
    title: 'Record expenses and revenues',
    description:
      'Log invoices directly into your budget and allocate funds effortlessly across all your Accounting Centers.',
    image: HowItWorksImage3,
    icon: HowItWorksIcon3
  },
  {
    title: 'Log work hours, they become real cost automatically',
    description:
      'Log daily working hours to automatically calculate labor costs and deduct them from your budget in real time.',
    image: HowItWorksImage4,
    icon: HowItWorksIcon4
  },
  {
    title: 'Validator confirms. Budget updates in real time.',
    description:
      'Once validators approve invoices and timesheets, your budget updates instantly with actionable insights.',
    image: HowItWorksImage5,
    icon: HowItWorksIcon5
  }
];

const LG_MIN_WIDTH = 1024;

const useMinWidth = (minWidth: number) => {
  const query = `(min-width: ${minWidth}px)`;
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

const STEP_SLOT_SIZE = 24;
const STEP_SLOT_GAP = 12;
const CARD_GAP = 40;
const CARD_PEEK = 72;
const CARD_TIP_HEIGHT = CARD_PEEK - CARD_GAP;
const STEP_TRANSITION = { duration: 0.7, ease: [0.22, 1, 0.36, 1] } as const;

const HowItWorksSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardSizerRef = useRef<HTMLDivElement>(null);
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  const mobileScrollIntent = useRef<number | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [cardHeight, setCardHeight] = useState(0);
  const motionEnabled = useMinWidth(LG_MIN_WIDTH);
  const motionEnabledRef = useRef(motionEnabled);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });

  motionEnabledRef.current = motionEnabled;

  useMotionValueEvent(scrollYProgress, 'change', (progress) => {
    if (!motionEnabledRef.current) return;

    const step = Math.min(HOW_IT_WORKS_STEPS.length - 1, Math.floor(progress * HOW_IT_WORKS_STEPS.length));
    setActiveStep(Math.max(0, step));
  });

  useEffect(() => {
    if (!motionEnabled) {
      setActiveStep(0);
      mobileScrollIntent.current = null;
      if (mobileScrollerRef.current) mobileScrollerRef.current.scrollLeft = 0;
    }
  }, [motionEnabled]);

  useLayoutEffect(() => {
    const sizer = cardSizerRef.current;
    if (!sizer) return;

    const observer = new ResizeObserver(([entry]) => setCardHeight(entry.contentRect.height));
    observer.observe(sizer);
    return () => observer.disconnect();
  }, []);

  const scrollToStep = (step: number) => {
    const section = sectionRef.current;
    if (!section) return;

    const scrollableDistance = section.offsetHeight - window.innerHeight;
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const stepCenter = (step + 0.5) / HOW_IT_WORKS_STEPS.length;
    window.scrollTo({ top: sectionTop + scrollableDistance * stepCenter, behavior: 'smooth' });
  };

  const onMobileScroll = () => {
    const scroller = mobileScrollerRef.current;
    if (!scroller || scroller.clientWidth === 0) return;

    const index = Math.min(
      HOW_IT_WORKS_STEPS.length - 1,
      Math.max(0, Math.round(scroller.scrollLeft / scroller.clientWidth))
    );

    if (mobileScrollIntent.current !== null) {
      if (index !== mobileScrollIntent.current) return;
      mobileScrollIntent.current = null;
    }

    setActiveStep((current) => (current === index ? current : index));
  };

  const scrollMobileToStep = (step: number) => {
    const scroller = mobileScrollerRef.current;
    setActiveStep(step);
    if (!scroller || scroller.clientWidth === 0) return;

    const left = step * scroller.clientWidth;
    if (Math.round(scroller.scrollLeft) === left) {
      mobileScrollIntent.current = null;
      return;
    }

    mobileScrollIntent.current = step;
    scroller.scrollTo({ left, behavior: 'smooth' });
  };

  const currentStep = HOW_IT_WORKS_STEPS[activeStep];

  return (
    <MotionConfig reducedMotion={motionEnabled ? 'user' : 'always'}>
      <section
        id="how-it-works"
        ref={sectionRef}
        className={`relative bg-[#121214] scroll-mt-24 ${motionEnabled ? 'h-[350vh]' : ''}`}
        aria-label="How it works"
      >
        <div
          className={`${
            motionEnabled ? 'sticky top-0 h-dvh' : ''
          } flex flex-col justify-center px-4 lg:px-[64px] py-[80px] lg:py-[60px] pt-[80px] lg:pt-[120px] overflow-hidden`}
        >
          <div className="w-full max-w-[1312px] mx-auto space-y-[48px]">
            <div className="relative z-10 space-y-3">
              <div className="space-y-[20px]">
                <div className="py-1.5 px-4 rounded-full h-[34px] bg-background w-fit mx-auto text-[#383838CC] text-sm leading-[22px] tracking-[-0.42px] shadow-[0px_4px_12px_0px_#0000001A]">
                  How it works
                </div>
                <h2 className="text-center text-[32px] font-medium leading-[40px] tracking-[-0.96px] text-white lg:text-[48px] lg:leading-[58px] lg:tracking-[-2.88px]">
                  Set it up once, and let it run itself.
                </h2>
              </div>
              <p className="text-white text-center text-base leading-[24px] tracking-[-0.32px]">
                Set the structure once, then let invoices and work hours flow through it automatically
              </p>
            </div>

            <div className="lg:hidden space-y-10">
              <div
                ref={mobileScrollerRef}
                onScroll={onMobileScroll}
                onPointerDown={() => {
                  mobileScrollIntent.current = null;
                }}
                className="flex snap-x snap-mandatory overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                aria-label="How it works steps"
              >
                {HOW_IT_WORKS_STEPS.map((step, index) => (
                  <div key={step.image} className="w-full shrink-0 snap-start" aria-hidden={index !== activeStep}>
                    <img
                      src={step.image}
                      alt={index === activeStep ? step.title : ''}
                      className="mx-auto block w-full md:max-w-[500px]"
                    />
                  </div>
                ))}
              </div>

              <div className="flex flex-col items-center gap-8">
                <div className="relative flex w-full max-w-[148px] items-center justify-between">
                  <span className="pointer-events-none absolute left-3 right-3 top-1/2 h-[2px] -translate-y-1/2 bg-[#383838CC]" />
                  {HOW_IT_WORKS_STEPS.map((step, index) => (
                    <button
                      key={step.title}
                      type="button"
                      onClick={() => scrollMobileToStep(index)}
                      aria-label={`Step ${index + 1}: ${step.title}`}
                      aria-current={index === activeStep ? 'step' : undefined}
                      className="relative z-10 flex size-6 items-center justify-center"
                    >
                      {index === activeStep ? (
                        <span className="inline-flex size-5 items-center justify-center rounded-[8px] bg-white text-base font-semibold leading-[18px] tracking-[-0.32px] text-[#0A0A0A]">
                          {index + 1}
                        </span>
                      ) : (
                        <span
                          className={`block size-2 rounded-full ${index < activeStep ? 'bg-white' : 'bg-[#383838CC]'}`}
                        />
                      )}
                    </button>
                  ))}
                </div>

                <div key={currentStep.title} className="flex max-w-[320px] flex-col items-center gap-4 text-center">
                  <div className="flex size-12 items-center justify-center rounded-[14px] bg-primary">
                    <img src={currentStep.icon} alt="" />
                  </div>
                  <div className="space-y-3">
                    <p className="text-base font-medium leading-[24px] tracking-[-0.32px] text-white">
                      {currentStep.title}
                    </p>
                    <p className="text-sm font-normal leading-[22px] tracking-[-0.42px] text-white">
                      {currentStep.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden lg:flex lg:flex-row gap-10 lg:max-w-[1100px] xl:max-w-[1200px] 2xl:max-w-[1320px] mx-auto">
              <div className="p-4 flex-1 max-w-[300px] flex items-center justify-end">
                <div className="relative flex flex-col gap-3">
                  <div className="absolute h-[calc(100%-24px)] top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-[#383838CC] w-[2px]" />

                  {HOW_IT_WORKS_STEPS.map((step, index) => (
                    <button
                      key={step.title}
                      type="button"
                      onClick={() => (motionEnabled ? scrollToStep(index) : setActiveStep(index))}
                      aria-label={`Step ${index + 1}: ${step.title}`}
                      aria-current={index === activeStep ? 'step' : undefined}
                      className="relative w-6 h-6 flex items-center justify-center"
                    >
                      <motion.span
                        className="size-2 bg-[#383838CC] rounded-full block"
                        animate={{ opacity: index === activeStep ? 0 : 1 }}
                        transition={STEP_TRANSITION}
                      />
                    </button>
                  ))}

                  <motion.div
                    className="pointer-events-none absolute top-0 left-0 w-6 h-6 flex items-center justify-center"
                    initial={false}
                    animate={{ y: activeStep * (STEP_SLOT_SIZE + STEP_SLOT_GAP) }}
                    transition={STEP_TRANSITION}
                  >
                    <div className="absolute left-0 -translate-x-[58px] size-12 rounded-[14px] bg-primary">
                      {HOW_IT_WORKS_STEPS.map((step, index) => (
                        <motion.img
                          key={step.icon}
                          src={step.icon}
                          alt=""
                          className="absolute inset-0 m-auto"
                          initial={false}
                          animate={{ opacity: index === activeStep ? 1 : 0, scale: index === activeStep ? 1 : 0.8 }}
                          transition={STEP_TRANSITION}
                        />
                      ))}
                    </div>
                    <span className="z-10 size-5 inline-flex items-center justify-center text-[#0A0A0A] font-semibold text-base tracking-[-0.32px] leading-[18px] bg-white rounded-[8px]">
                      {activeStep + 1}
                    </span>
                  </motion.div>
                </div>
              </div>

              <div className="flex-1">
                <div
                  className="relative overflow-hidden"
                  style={{
                    paddingTop: CARD_PEEK,
                    paddingBottom: CARD_PEEK,
                    marginTop: -CARD_PEEK,
                    marginBottom: -CARD_PEEK
                  }}
                >
                  <div className="relative">
                    <div ref={cardSizerRef} className="invisible p-3" aria-hidden>
                      <img src={HOW_IT_WORKS_STEPS[0].image} alt="" />
                    </div>

                    {HOW_IT_WORKS_STEPS.map((step, index) => {
                      const offset = index - activeStep;
                      const isActive = offset === 0;

                      return (
                        <motion.div
                          key={step.image}
                          className="absolute inset-x-0 top-0 p-3 rounded-[40px]"
                          initial={false}
                          animate={{
                            y: offset * (cardHeight + CARD_GAP),
                            opacity: Math.abs(offset) > 1 ? 0 : 1
                          }}
                          transition={STEP_TRANSITION}
                          aria-hidden={!isActive}
                        >
                          <motion.div
                            className="absolute inset-0 rounded-[inherit] bg-[#292929]"
                            initial={false}
                            animate={{ opacity: isActive ? 1 : 0 }}
                            transition={STEP_TRANSITION}
                          />
                          <motion.div
                            className="absolute inset-0 rounded-[inherit]"
                            style={{
                              background: `linear-gradient(${offset < 0 ? '0deg' : '180deg'}, #292929 0, #121214 ${CARD_TIP_HEIGHT}px)`
                            }}
                            initial={false}
                            animate={{ opacity: isActive ? 0 : 1 }}
                            transition={STEP_TRANSITION}
                          />
                          <motion.img
                            src={step.image}
                            alt={isActive ? step.title : ''}
                            className="relative"
                            initial={false}
                            animate={{ opacity: isActive ? 1 : 0 }}
                            transition={STEP_TRANSITION}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex-1 max-w-[300px] flex items-center">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentStep.title}
                    className="space-y-4 max-w-[225px]"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <p className="text-white font-medium text-[20px] leading-[30px] tracking-[-0.4px]">
                      {currentStep.title}
                    </p>
                    <p className="text-white font-normal text-sm leading-[22px] tracking-[-0.42px]">
                      {currentStep.description}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
};

export default HowItWorksSection;
